/**
 * Request-size chunking for issue EXPORTS (Jira / Linear / Notion / Asana /
 * Trello), shared by the three surfaces that send them: the extension
 * (lib/api/integrations.ts), the dashboard (lib/api/integrations.ts) and the
 * team-review drawer (review-app/lib/exportIssues.ts).
 *
 * Every export route caps `ticketIds` at 50 per request
 * (`routes/integrations/_export-core.ts` for Jira/Linear/Asana, plus the
 * bespoke Trello and Notion routes). The clients, though, sent EVERY selected
 * id in one request, so a user who selected 51+ issues got a 400 whose copy
 * ("Invalid request. Please check your input and try again.") named nothing
 * they could act on. Measured 2026-09-09: an external Jira user retried the
 * same export five times in 75s before giving up (the Failure Burst workflow
 * paged on it). Splitting here keeps "export the selection" meaning the whole
 * selection.
 *
 * TWO constants, deliberately, because they answer different questions.
 * EXPORT_MAX_IDS_PER_REQUEST is what the SERVER will accept;
 * EXPORT_CLIENT_CHUNK_SIZE is how much work one request is worth WAITING for.
 * Chunking at the cap made every request a maximally slow request: `runExport`
 * creates provider issues sequentially, so a 50-id chunk is ~93s at Jira's
 * median pace and ~171s at its worst, against a client ceiling that was 30s
 * until `5526e1d0b` raised the /export leaf to 300s. The first chunk failing
 * rethrows (see runChunkedExport), so at the cap a single slow request
 * reported total failure over issues the server had really created.
 */
/**
 * Keep in sync with the `.max(50)` on `ticketIds` in the three export route
 * schemas. `packages/server/src/__tests__/unit/exportCap.parity.spec.ts`
 * greps those routes and fails when any of them drifts from this number.
 */
export const EXPORT_MAX_IDS_PER_REQUEST = 50;
/**
 * How many ids ONE export request carries. Below the server cap on purpose.
 *
 * Sized off a read-only production measurement taken 2026-09-11 from
 * `tickets.exported_to` stamps on ep-solitary-fog, clustering consecutive
 * creates per project into requests (a gap over 60s starts a new one):
 *
 *   jira    100 creates, per-create median 1.85s, p90 2.22s, max 3.43s
 *   trello   11 creates, per-create median 3.11s, max 4.55s  <- worst overall
 *   notion    5 creates, per-create median 0.88s, max 1.55s
 *
 * At 10 the worst observed chunk is ~46s against the 300s long-endpoint
 * ceiling (`AI_PROXY_TIMEOUT_MS`), a 6.6x margin; at the 50 cap it was ~227s,
 * a 1.3x margin. Raise this only against a fresh measurement, never from feel:
 * the ceiling is the extension's, and the providers set the pace.
 */
export const EXPORT_CLIENT_CHUNK_SIZE = 10;
/** Split `ids` into request-sized chunks, preserving order. */
export function chunkExportIds(ids, size = EXPORT_CLIENT_CHUNK_SIZE) {
    const chunks = [];
    for (let i = 0; i < ids.length; i += size) {
        chunks.push(ids.slice(i, i + size));
    }
    return chunks;
}
function failureMessage(err) {
    return err instanceof Error ? err.message : String(err);
}
/**
 * Send `ticketIds` as sequential chunks of at most `size`, merging the
 * per-ticket rows in input order.
 *
 * Contract:
 * - At most `size` ids ever reach `sendChunk`, so a selection of any length
 *   satisfies the server cap.
 * - `size` or fewer ids is ONE call carrying exactly those ids: byte-identical
 *   to the un-chunked request every existing test pins.
 * - Sequential, never parallel: the export routes share the caller's write
 *   budget with everything else the user is doing, and the provider (Notion
 *   in particular) rate-limits per second.
 * - Partial failure resolves, it does not reject: once a chunk has created
 *   real issues in the provider, a later chunk failing must not read as
 *   "nothing was exported" (the caller would toast a generic failure over
 *   tickets that now exist). The ids of the failed chunk and every chunk
 *   after it come back as failure rows carrying the error's message, the
 *   same shape the server uses for a ticket it could not create, so the
 *   shared summarizer reports "N exported, M failed" honestly.
 * - A failure on the FIRST chunk rethrows: nothing was created, and that is
 *   exactly the old single-request failure path (auth, connection, refusal).
 */
export async function runChunkedExport(ticketIds, sendChunk, size = EXPORT_CLIENT_CHUNK_SIZE) {
    const chunks = chunkExportIds(ticketIds, size);
    const results = [];
    for (let i = 0; i < chunks.length; i += 1) {
        try {
            const response = await sendChunk(chunks[i]);
            results.push(...response.results);
        }
        catch (err) {
            if (i === 0)
                throw err;
            const error = failureMessage(err);
            for (const chunk of chunks.slice(i)) {
                for (const ticketId of chunk)
                    results.push({ ticketId, success: false, error });
            }
            break;
        }
    }
    return { results };
}
//# sourceMappingURL=exportChunking.js.map